// The five questions the Днес screen answers (04 §1).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isConfigured, isDemoMode, supabase } from '../../lib/supabase.js';

export interface FollowUp {
  id: number;
  lead_id: number;
  business_name: string;
  next_follow_up: string;
  outcome: string;
  notes: string | null;
}

export interface UnworkedLead {
  id: number;
  business_name: string;
  area: string | null;
  city_key?: string | null;
  lead_score: number | null;
  tier: string | null;
}

export interface SinceLastVisit {
  since: string | null;
  newLeads: number;
  newDocuments: number;
}

export function useLastSeen() {
  return useQuery({
    queryKey: ['last_seen'],
    staleTime: Infinity,
    queryFn: async (): Promise<string | null> => {
      if (!isConfigured || isDemoMode) {
        return '2026-09-08T16:00:00Z';
      }
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth.user?.id;
      if (userId === undefined) return null;

      // The key is user_id (0000_bootstrap). Filtering on a column the table does not have
      // was a silent 42703 and "Ново от посещението" stayed at zero forever.
      const { data, error } = await supabase
        .from('profiles')
        .select('last_seen_at')
        .eq('user_id', userId)
        .maybeSingle();
      if (error) throw new Error(error.message);

      const previous = (data?.last_seen_at as string | null) ?? null;
      const { error: writeError } = await supabase
        .from('profiles')
        .update({ last_seen_at: new Date().toISOString() })
        .eq('user_id', userId);
      if (writeError) throw new Error(writeError.message);
      return previous;
    },
  });
}

export function useSinceLastVisit(since: string | null) {
  return useQuery({
    queryKey: ['since', since],
    queryFn: async (): Promise<SinceLastVisit> => {
      if (!isConfigured || isDemoMode) {
        return { since, newLeads: 3, newDocuments: 2 };
      }
      if (since === null) return { since, newLeads: 0, newDocuments: 0 };

      const [leads, documents] = await Promise.all([
        supabase
          .from('leads')
          .select('id', { count: 'exact', head: true })
          .is('parent_lead_id', null)
          .not('business_status', 'in', '("CLOSED_PERMANENTLY","REMOVED_FROM_GOOGLE")')
          .gt('created_at', since),
        supabase
          .from('documents')
          .select('id', { count: 'exact', head: true })
          .gt('generated_at', since),
      ]);

      return {
        since,
        newLeads: leads.count ?? 0,
        newDocuments: documents.count ?? 0,
      };
    },
  });
}

export function useFollowUpsDue() {
  return useQuery({
    queryKey: ['follow_ups'],
    queryFn: async (): Promise<FollowUp[]> => {
      if (!isConfigured || isDemoMode) {
        return [
          {
            id: 1,
            lead_id: 1,
            business_name: 'Дентално Студио Д-р Иванова',
            next_follow_up: 'Днес, 14:00',
            outcome: 'уговорена среща',
            notes: 'Демонстрация на модула за резервации и онлайн график',
          },
        ];
      }

      // The owner's calendar day, not UTC's: at 01:00 in Sofia the UTC date is still yesterday
      // and today's follow-ups were hidden until 03:00.
      const today = localDate();
      const { data, error } = await supabase
        .from('outreach_events')
        .select('id, lead_id, next_follow_up, outcome, notes, leads(business_name)')
        .is('done_at', null)
        .not('next_follow_up', 'is', null)
        .lte('next_follow_up', today)
        .order('next_follow_up', { ascending: true })
        .limit(50);

      if (error) throw new Error(error.message);
      return (data ?? []).map((row) => {
        const record = row as unknown as Record<string, unknown>;
        const lead = record['leads'] as { business_name?: string } | null;
        return {
          id: record['id'] as number,
          lead_id: record['lead_id'] as number,
          business_name: lead?.business_name ?? `#${String(record['lead_id'])}`,
          next_follow_up: record['next_follow_up'] as string,
          outcome: record['outcome'] as string,
          notes: (record['notes'] as string | null) ?? null,
        };
      });
    },
  });
}

export function useCompleteFollowUp() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (eventId: number) => {
      if (!isConfigured || isDemoMode) return;
      const { error } = await supabase.rpc('complete_follow_up', { p_event_id: eventId });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['follow_ups'] });
      void client.invalidateQueries({ queryKey: ['outreach'] });
    },
  });
}

export function useTopUnworked() {
  return useQuery({
    queryKey: ['top_unworked'],
    queryFn: async (): Promise<UnworkedLead[]> => {
      if (!isConfigured || isDemoMode) {
        return [
          { id: 2, business_name: 'Дента Център Лазур', area: 'Лазур', lead_score: 88, tier: 'A' },
          {
            id: 6,
            business_name: 'Д-р Димитров - Естетична Стоматология',
            area: 'Меден Рудник',
            lead_score: 85,
            tier: 'A',
          },
          {
            id: 3,
            business_name: 'Специализирана Клиника Бургас Дент',
            area: 'Център',
            lead_score: 74,
            tier: 'B',
          },
          {
            id: 4,
            business_name: 'Дентална Практика Д-р Георгиев',
            area: 'Възраждане',
            lead_score: 68,
            tier: 'B',
          },
        ];
      }

      const { data: contacted } = await supabase.from('outreach_events').select('lead_id');
      const worked = new Set((contacted ?? []).map((row) => row.lead_id as number));

      const { data, error } = await supabase
        .from('leads')
        .select('id, business_name, area, city_key, lead_score, tier')
        .eq('do_not_contact', false)
        .is('parent_lead_id', null)
        .not('business_status', 'in', '("CLOSED_PERMANENTLY","REMOVED_FROM_GOOGLE")')
        .not('lead_score', 'is', null)
        .order('lead_score', { ascending: false })
        .limit(50);

      if (error) throw new Error(error.message);
      return ((data ?? []) as unknown as UnworkedLead[])
        .filter((lead) => !worked.has(lead.id))
        .slice(0, 5);
    },
  });
}

export function useOpenReviewCount() {
  return useQuery({
    queryKey: ['manual_review_open'],
    queryFn: async (): Promise<number> => {
      if (!isConfigured || isDemoMode) {
        return 2;
      }
      const { count } = await supabase
        .from('manual_review')
        .select('id', { count: 'exact', head: true })
        .eq('resolved', false);
      return count ?? 0;
    },
  });
}

/** YYYY-MM-DD in the browser's own time zone. */
function localDate(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
